import { Injectable } from "@angular/core";
import { AbstractControl, AsyncValidator, ValidationErrors, ValidatorFn } from "@angular/forms";
import { Observable } from "rxjs";
import { Acumatica, AcumaticaFieldFormat } from "../acumatica-field-decorator";
import { AcumaticaRequestOptions, AcumaticaService } from "../acumatica.service";
import { AcumaticaEntity, AcumaticaEntityService } from "./acumatica-entity";
import { AcumaticaLoadingOptions } from "../loading-indicator/AcumaticaLoadingOptions";

@Injectable({
    providedIn: 'root'
})
export class WarehouseService extends AcumaticaEntityService {
    private WhseLocationCacheTimeInDays = 30;

    constructor(
        protected acumatica: AcumaticaService
    ) {
        super(acumatica, 'Warehouse', Warehouse);
    }

    get(
        WarehouseID: string,
        options: AcumaticaRequestOptions = {}
    ): Observable<Warehouse> {
        return new Observable<Warehouse>(observer => {
            super.getEntity(WarehouseID, {
                ...{
                    loader: { message: "Getting Warehouse" }
                },
                ...options
            }).subscribe(observer);
        })
    }

    /** returns a string array of active Location IDs for the specified warehouse */
    getLocations(warehouseID: string, refreshFromServer: boolean = false): Observable<string[]> {
        return new Observable<string[]>(subscriber => {
            let asOfKey = 'whse-locations-as-of-' + warehouseID;
            let lastFetchDate = parseInt(localStorage.getItem(asOfKey) || '0');
            let today = new Date();
            if (today.toYYYYMMDD() - lastFetchDate > this.WhseLocationCacheTimeInDays) refreshFromServer = true;

            if (refreshFromServer) this.clearCachedLocations(warehouseID)
            let locations: string[];

            const key = this.warehouseLocationCacheKey(warehouseID);
            let warehouseLocations = localStorage.getItem(key);
            if (warehouseLocations !== null) {
                locations = JSON.parse(warehouseLocations);
                subscriber.next(locations);
                subscriber.complete();
            } else {
                this.get(warehouseID, {
                    loader: 'Refreshing Warehouse Locations',
                    expand: 'Locations'
                }).subscribe(warehouse => {
                    const locations = warehouse.Locations!
                        .filter(location => location.Active)
                        .map(location => location.LocationID);
                    localStorage.setItem(key, JSON.stringify(locations));
                    localStorage.setItem(asOfKey, today.toYYYYMMDD().toString());

                    subscriber.next(locations);
                    subscriber.complete();
                });
            }
        });
    }

    /** clear the locally stored list of warehouse locations */
    clearCachedLocations(warehouseID: string) {
        const key = this.warehouseLocationCacheKey(warehouseID);
        localStorage.removeItem(key);
    }

    private warehouseLocationCacheKey(warehouseID: string) {
        return 'whse-locations-array-' + warehouseID;
    }

    // updateLocation(warehouseID: string, locationID: string, settings: WarehouseLocationSettings, loader_options: AcumaticaLoadingOptions | null = null): Observable<void> {
    //     const entity = new AcumaticaEntity({ WarehouseID: warehouseID });
    //     const params = new AcumaticaEntity(settings);
    //     (<any>params).LocationID = locationID;
    //     return super.action(entity, 'UpdateLocationSettings', params, loader_options);
    // }
}

export class WarehouseLocation extends AcumaticaEntity {
    @Acumatica() Active!: boolean;
    @Acumatica() Description!: string;
    @Acumatica() LocationID!: string;
}

export class Warehouse extends AcumaticaEntity {
    @Acumatica() WarehouseID!: string;
    @Acumatica() Description!: string;

    // fields used by Sales Rep component for filling in custom address on
    // binder card fulfillment orders when shipping to the Sales Rep
    @Acumatica() Attention: string | null = null;
    @Acumatica() AccountName: string | null = null;
    @Acumatica() Phone1: string | null = null;
    @Acumatica() Email: string | null = null;
    @Acumatica() AddressLine1: string | null = null;
    @Acumatica() AddressLine2: string | null = null;
    @Acumatica() City: string | null = null;
    @Acumatica() State: string | null = null;
    @Acumatica() Country: string | null = null;
    @Acumatica() PostalCode: string | null = null;

    @Acumatica(WarehouseLocation) Locations?: WarehouseLocation[];
}

export class WarehouseLocationSettings {
    public InclQtyAvail?: boolean;
}